// client/src/pages/admin/DesksBoard.tsx
// Admin-only Desks & Assignments board, modeled on the Dev Team dashboard:
//   • Columns: Inbox Triage holds Admin Team emails; Open / In Progress / Completed are an
//     admin-hq-MANAGED status per JOB (dbo.desk_job_status, moved by the card's Lane control,
//     default In Progress). The Admin Pool column is assignment-driven — jobs sitting on the
//     Admin Pool desk. Assembly/Shipping/SP Binder Creation show as a colored PE-status chip,
//     not their own column.
//   • "Assigned To" = who's actually working it = PE Job_CurrentStaff (a dropdown of
//     admin members + the Admin Pool).
// Cards are color-coded by office (left bar) and department (tag).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { LayoutGrid, RefreshCw, Search, User, Calendar, ExternalLink, Mail, Reply, X, Eye, MoreHorizontal } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { AttachmentChips, InboxThreadViewer } from './InboxEmailParts';
import { fetchDesks, assignJob, setDeskStatus, saveDeskNotes, DesksData, DeskJob, Lane } from '../../services/desks';
import { quoteOriginal, withQuote } from '../../utils/emailQuote';
import { fetchInbox, fetchInboxAssignees, assignInboxItem, setInboxStatus, emailInboxSender, setInboxImportant, setInboxRead, fetchInboxReaders, InboxItem, InboxPerson, InboxReader, InboxStatus } from '../../services/adminInbox';
import DeskNotes from './DeskNotes';
import { officeColor, deptColor, peStageColor, deliveryColor, stepColor } from './desksColors';

const STEP_LABEL = { pending: 'Pending', active: 'In progress', done: 'Done' } as const;

// Lanes a JOB card's Lane dropdown offers. Inbox Triage (emails) and Admin Pool are NOT
// here: Inbox Triage holds Admin Team mail, and the Admin Pool column is assignment-driven
// (jobs on the Admin Pool desk — move a job there via "Assigned To"). Untriaged jobs default
// to In Progress.
const JOB_LANES: { key: Lane; label: string }[] = [
  { key: 'open',        label: 'Open' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed',   label: 'Completed' },
];

// Board column order. Inbox Triage (Admin Team emails) leads; the rest are the job lanes.
// Assembly/Shipping/SP Binder Creation aren't their own columns — those jobs sit in Admin
// Pool with their PE status shown as a colored chip.
const BOARD_COLUMNS: { key: string; label: string; color: string }[] = [
  { key: 'inbox_triage',      label: 'Inbox Triage',      color: '#c084fc' },
  { key: 'important_reading', label: 'Important Reading', color: '#f4b740' }, // email-only; read-tracked
  { key: 'open',              label: 'Open',              color: '#7aa2ff' },
  { key: 'in_progress',       label: 'In Progress',       color: '#ffd166' },
  { key: 'admin_pool',        label: 'Admin Pool',        color: '#8fa3b8' },
  { key: 'completed',         label: 'Completed',         color: '#57d38c' },
];

// Lanes an EMAIL card's Lane dropdown offers. Important Reading is an admin-hq flag
// (leadership-only); the rest map to the email's Feedback status.
const EMAIL_LANES: { key: string; label: string }[] = [
  { key: 'inbox_triage',      label: 'Inbox Triage' },
  { key: 'important_reading', label: 'Important Reading' },
  { key: 'in_progress',       label: 'In Progress' },
  { key: 'completed',         label: 'Completed' },
];
const EMAIL_STATUS_OF: Record<string, InboxStatus> = { inbox_triage: 'inbox_triage', in_progress: 'in_progress', completed: 'resolved' };

// Which board column an email lands in. The Important flag wins while the email is open;
// one handled from Important Reading leaves the board.
function emailColumnOf(item: InboxItem): string | null {
  if (item.important) return item.status === 'resolved' ? null : 'important_reading';
  return item.status === 'resolved' ? 'completed' : item.status;
}

// PE Job_CurrentStaff sentinel for "no staff allocated" (unassigned). Jobs on this
// desk fall out of the board query, so a released job leaves the board on refresh.
const NO_STAFF = 0;

const selectStyle: React.CSSProperties = {
  background: 'transparent', color: 'inherit', border: '1px solid var(--border)',
  borderRadius: 8, padding: '6px 8px', fontSize: 12,
};
// The <select>'s closed state inherits our dark-theme text color, which reads fine
// against the card background. But the OPEN dropdown list is rendered natively by the
// browser on a light popup background, so that same light/inherited color goes nearly
// invisible there. Give <option>s their own explicit dark-on-light contrast.
const optionStyle: React.CSSProperties = { color: '#111827', background: '#fff' };

function fmtDue(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

// Compact actor label from an email (the part before @); full email shown as a tooltip.
const actorLabel = (email: string) => (email || '').split('@')[0] || email;

const ARCHIVE_DAYS = 14;
const SHOW_ARCHIVED_LS = 'desks.showArchived';
const ARCHIVED_COL = { key: 'archived', label: 'Archived', color: '#8090a0' } as const;

// A job auto-archives once it's sat in the Completed lane for ARCHIVE_DAYS.
function isArchived(job: DeskJob): boolean {
  if (job.lane !== 'completed' || !job.laneUpdatedAt) return false;
  const t = Date.parse(job.laneUpdatedAt);
  return !Number.isNaN(t) && Date.now() - t > ARCHIVE_DAYS * 86400000;
}

// Which board column a job lands in: archived (completed >14d) first, then the Admin Pool
// column if it's on the Admin Pool desk (assignment-driven), otherwise its admin-managed
// lane (default In Progress).
function bucketOf(job: DeskJob, poolIndex: number): string {
  if (isArchived(job)) return 'archived';
  if (job.staffIndex === poolIndex) return 'admin_pool';
  // Inbox Triage is email-only and admin_pool isn't a manual lane — either falls to In Progress.
  return (job.lane === 'inbox_triage' || job.lane === 'admin_pool') ? 'in_progress' : job.lane;
}

export default function DesksBoard({ audience = 'leadership' }: { audience?: 'leadership' | 'team' }) {
  const { ready, isAdminLeadership, isAdminTeam, email } = useAuth();
  const teamView = audience === 'team';
  // Desks & Assignments is leadership-only (ADMIN_LEADERSHIP_EMAILS). The Admin Team
  // Dashboard is open to leadership AND the admin team (ADMIN_TEAM_EMAILS); the API
  // narrows what team-only members can see and do (Admin Pool + Completed jobs).
  const canView = teamView ? (isAdminLeadership || isAdminTeam) : isAdminLeadership;
  const canManageImportant = !teamView && isAdminLeadership;
  const title = teamView ? 'Admin Team Dashboard' : 'Desks & Assignments';
  const [data, setData] = useState<DesksData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyJob, setBusyJob] = useState<number | null>(null);
  const [inbox, setInbox] = useState<InboxItem[]>([]);
  const [inboxError, setInboxError] = useState<string | null>(null);
  const [inboxPeople, setInboxPeople] = useState<InboxPerson[]>([]);
  const [busyEmail, setBusyEmail] = useState<number | null>(null);
  const [compose, setCompose] = useState<{ item: InboxItem; to: string; cc: string; subject: string; body: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [threadView, setThreadView] = useState<InboxItem | null>(null); // email thread viewer
  const [readers, setReaders] = useState<{ item: InboxItem; list: InboxReader[]; loading: boolean } | null>(null);
  // Cards whose Notes editor is open ('j<jobIdx>' / 'm<bugReportId>'), and the ones just moved to
  // Completed (their editor opens with a "add a note?" nudge). Kept here, not in the card, because
  // a card re-mounts in its new column when its lane changes.
  const [notesOpen, setNotesOpen] = useState<Set<string>>(new Set());
  const [completedNudge, setCompletedNudge] = useState<Set<string>>(new Set());

  const [search, setSearch] = useState('');
  const [officeFilter, setOfficeFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all'); // 'all' | staffIndex (as string)
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | PE workStatusDesc
  const [showArchived, setShowArchived] = useState<boolean>(() => {
    try { return localStorage.getItem(SHOW_ARCHIVED_LS) === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem(SHOW_ARCHIVED_LS, showArchived ? '1' : '0'); } catch { /* ignore */ } }, [showArchived]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInboxError(null);
    try {
      const [d, inboxItems, inboxPpl] = await Promise.all([
        fetchDesks(),
        // Don't fail the whole board on an inbox error, but don't hide it as "No mail" either.
        fetchInbox().catch((e: any) => { setInboxError(e?.message ?? 'Failed to load the Admin Team inbox.'); return [] as InboxItem[]; }),
        fetchInboxAssignees().catch(() => [] as InboxPerson[]),
      ]);
      setData(d);
      setInbox(inboxItems);
      setInboxPeople(inboxPpl);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load the board.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (ready && canView) load();
  }, [ready, canView, load]);

  // "Assigned To" options: Admin Pool first, then people by name.
  const assignees = useMemo(() => {
    if (!data) return [] as { staffIndex: number; name: string }[];
    return [
      { staffIndex: data.poolIndex, name: 'Admin Pool' },
      ...[...data.people].sort((a, b) => a.name.localeCompare(b.name)).map((p) => ({ staffIndex: p.staffIndex, name: p.name })),
      { staffIndex: NO_STAFF, name: '— No staff —' }, // release the desk (unassign)
    ];
  }, [data]);
  const nameOf = useCallback((staffIndex: number) => assignees.find((a) => a.staffIndex === staffIndex)?.name ?? String(staffIndex), [assignees]);

  // Practice Engine web base for per-card "Open in PE" deep links.
  const peBase = data?.peBase ?? '';
  // The Admin Pool desk's StaffIndex — jobs assigned here show in the Admin Pool lane.
  const poolIndex = data?.poolIndex ?? -1;

  // Admin Team inbox (the Inbox Triage lane). Cards respond to the text search only —
  // office/dept/person/PE-status filters are job attributes and don't apply to email.
  const inboxNameOf = useCallback((email: string) => inboxPeople.find((p) => p.email === email)?.name ?? email, [inboxPeople]);
  const inboxFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return inbox;
    return inbox.filter((it) => `${it.from} ${it.subject} ${it.preview}`.toLowerCase().includes(q));
  }, [inbox, search]);
  // Leadership's read-receipt roster for the open item: every admin (pool desks excluded)
  // with whether/when they acknowledged it, plus any reader who isn't on the roster.
  const readRoster = useMemo(() => {
    if (!readers || readers.loading) return [];
    const readAt = new Map(readers.list.map((r) => [r.email.toLowerCase(), r.readAt]));
    const rows = inboxPeople
      .filter((p) => !/\bpool\b/i.test(p.name))
      .map((p) => ({ email: p.email, name: p.name, read: readAt.has(p.email.toLowerCase()), readAt: readAt.get(p.email.toLowerCase()) ?? null }));
    const known = new Set(rows.map((r) => r.email.toLowerCase()));
    for (const r of readers.list) {
      if (!known.has(r.email.toLowerCase())) rows.push({ email: r.email, name: actorLabel(r.email), read: true, readAt: r.readAt });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [readers, inboxPeople]);

  const emailsByColumn = useMemo(() => {
    const m = new Map<string, InboxItem[]>();
    for (const it of inboxFiltered) {
      const col = emailColumnOf(it);
      if (col) m.set(col, [...(m.get(col) ?? []), it]);
    }
    return m;
  }, [inboxFiltered]);

  // Filter option lists.
  const officeName = useMemo(() => {
    const m = new Map<string, string>();
    for (const j of data?.jobs ?? []) if (j.officeCode) m.set(j.officeCode, j.office || j.officeCode);
    return m;
  }, [data]);
  const officeOptions = useMemo(() => [...officeName.keys()].sort(), [officeName]);
  const deptOptions = useMemo(() => Array.from(new Set((data?.jobs ?? []).map((j) => j.dept).filter(Boolean))).sort(), [data]);
  const statusOptions = useMemo(() => Array.from(new Set((data?.jobs ?? []).map((j) => j.workStatusDesc).filter(Boolean))).sort(), [data]);

  // Archived (completed >14d) is hidden unless the toggle is on OR a search/filter is
  // active (which still surfaces archived, like the Dev Team board).
  const filtersActive = search.trim() !== '' || officeFilter !== 'all' || deptFilter !== 'all' || personFilter !== 'all' || statusFilter !== 'all';
  const archivedVisible = showArchived || filtersActive;

  // Filtered jobs bucketed by column (lane, or 'archived').
  const jobsByBucket = useMemo(() => {
    const q = search.trim().toLowerCase();
    const m = new Map<string, DeskJob[]>();
    for (const col of BOARD_COLUMNS) m.set(col.key, []);
    m.set('archived', []);
    for (const j of data?.jobs ?? []) {
      if (officeFilter !== 'all' && j.officeCode !== officeFilter) continue;
      if (deptFilter !== 'all' && j.dept !== deptFilter) continue;
      if (personFilter !== 'all' && String(j.staffIndex) !== personFilter) continue;
      if (statusFilter !== 'all' && j.workStatusDesc !== statusFilter) continue;
      if (q && !`${j.clientName} ${j.jobName} ${j.jobCode} ${j.clientCode}`.toLowerCase().includes(q)) continue;
      const bucket = bucketOf(j, poolIndex);
      if (teamView && ['open', 'in_progress'].includes(bucket)) continue;
      if (bucket === 'archived' && !archivedVisible) continue;
      (m.get(bucket) ?? m.get('in_progress'))!.push(j);
    }
    // FIFO — oldest first, by the job's creation date.
    for (const list of m.values()) list.sort((a, b) => (Date.parse(a.createdAt) || 0) - (Date.parse(b.createdAt) || 0));
    return m;
  }, [data, search, officeFilter, deptFilter, personFilter, statusFilter, archivedVisible, poolIndex, teamView]);

  // The team view hides Open; its In Progress column holds only emails (jobs filtered above).
  const visibleColumns = BOARD_COLUMNS.filter((col) => !teamView || col.key !== 'open');
  const columns = archivedVisible ? [...visibleColumns, ARCHIVED_COL] : visibleColumns;
  const shownJobs = useMemo(() => [...jobsByBucket.values()].reduce((n, l) => n + l.length, 0), [jobsByBucket]);

  const patchJob = useCallback((jobIdx: number, patch: Partial<DeskJob>) => {
    setData((d) => (d ? { ...d, jobs: d.jobs.map((j) => (j.jobIdx === jobIdx ? { ...j, ...patch } : j)) } : d));
  }, []);

  // Moving a card to Completed opens its Notes editor so a note can be added right then (optional — Skip closes it).
  const nudgeNotes = useCallback((key: string) => {
    setNotesOpen((s) => new Set(s).add(key));
    setCompletedNudge((s) => new Set(s).add(key));
  }, []);

  const assign = useCallback(async (job: DeskJob, toStaffIndex: number) => {
    if (!data || toStaffIndex === job.staffIndex) return;
    setBusyJob(job.jobIdx);
    const prevStaff = job.staffIndex;
    const prevAssignedAt = job.assignedAt;
    patchJob(job.jobIdx, { staffIndex: toStaffIndex, assignedAt: new Date().toISOString() });
    try {
      await assignJob(job.jobIdx, toStaffIndex);
      toast.success(`${job.clientName || job.clientCode} → ${nameOf(toStaffIndex)}`);
    } catch (e: any) {
      patchJob(job.jobIdx, { staffIndex: prevStaff, assignedAt: prevAssignedAt });
      toast.error(e?.message ?? 'Assign failed');
    } finally {
      setBusyJob(null);
    }
  }, [data, patchJob, nameOf]);

  const moveLane = useCallback(async (job: DeskJob, toLane: Lane) => {
    if (!data || toLane === job.lane) return;
    setBusyJob(job.jobIdx);
    const prevLane = job.lane;
    patchJob(job.jobIdx, { lane: toLane });
    try {
      await setDeskStatus(job.jobIdx, toLane);
    } catch (e: any) {
      patchJob(job.jobIdx, { lane: prevLane });
      toast.error(e?.message ?? 'Move failed');
      setBusyJob(null);
      return;
    }
    if (toLane === 'completed') nudgeNotes(`j${job.jobIdx}`);

    // Completing a job can release it from the current desk (no staff allocated) —
    // the finished work leaves that person's desk. Optional: confirm first, and only
    // when it's still on a real desk. A released job drops off the board on refresh.
    if (toLane === 'completed' && job.staffIndex !== NO_STAFF) {
      const who = nameOf(job.staffIndex);
      const release = window.confirm(
        `Marked "${job.clientName || job.clientCode}" completed.\n\n` +
        `Also release it from ${who}'s desk (set to no staff allocated)?\n` +
        `It will drop off the board on the next refresh.`
      );
      if (release) {
        const prevStaff = job.staffIndex;
        const prevAssignedAt = job.assignedAt;
        patchJob(job.jobIdx, { staffIndex: NO_STAFF, assignedAt: '' });
        try {
          await assignJob(job.jobIdx, NO_STAFF);
          toast.success(`Released from ${who}'s desk — no staff allocated`);
        } catch (e: any) {
          patchJob(job.jobIdx, { staffIndex: prevStaff, assignedAt: prevAssignedAt });
          toast.error(e?.message ?? 'Release failed (still assigned)');
        }
      }
    }
    setBusyJob(null);
  }, [data, patchJob, nameOf, nudgeNotes]);

  const assignEmail = useCallback(async (item: InboxItem, email: string) => {
    if ((email || '') === (item.assignedTo || '')) return;
    setBusyEmail(item.bugReportId);
    const prev = item.assignedTo;
    setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId ? { ...it, assignedTo: email || null } : it)));
    try {
      await assignInboxItem(item.bugReportId, email, item.status);
      toast.success(email ? `${item.subject} → ${inboxNameOf(email)}` : 'Unassigned');
    } catch (e: any) {
      setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId ? { ...it, assignedTo: prev } : it)));
      toast.error(e?.message ?? 'Assign failed');
    } finally {
      setBusyEmail(null);
    }
  }, [inboxNameOf]);

  const openReply = useCallback((item: InboxItem) => {
    setCompose({ item, to: item.from, cc: '', subject: /^re:/i.test(item.subject) ? item.subject : `Re: ${item.subject}`, body: '' });
  }, []);

  const sendReply = useCallback(async () => {
    if (!compose) return;
    if (!compose.to.trim() || !compose.subject.trim() || !compose.body.trim()) {
      toast.error('To, subject, and message are all required.');
      return;
    }
    setSending(true);
    try {
      await emailInboxSender({ to: compose.to.trim(), cc: compose.cc.trim(), subject: compose.subject.trim(), body: withQuote(compose.body, quoteOriginal({ from: compose.item.from, when: compose.item.receivedAt, subject: compose.item.subject, body: compose.item.preview })) });
      const extra = compose.cc.split(/[;,\s]+/).filter(Boolean).length;
      toast.success(`Emailed ${compose.to.trim()}${extra ? ` (+${extra} cc'd)` : ''}`);
      setCompose(null);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to send the email.');
    } finally {
      setSending(false);
    }
  }, [compose]);

  const moveImportant = useCallback(async (item: InboxItem, important: boolean) => {
    setBusyEmail(item.bugReportId);
    setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId ? { ...it, important } : it)));
    try {
      await setInboxImportant(item.bugReportId, important);
      toast.success(important ? 'Moved to Important Reading' : 'Moved back to Inbox Triage');
    } catch (e: any) {
      setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId ? { ...it, important: !important } : it)));
      toast.error(e?.message ?? 'Move failed');
    } finally {
      setBusyEmail(null);
    }
  }, []);

  // An email card's Lane dropdown: Important Reading sets the flag; the rest set its status
  // (clearing the flag first when moving an item out of Important Reading).
  const moveEmail = useCallback(async (item: InboxItem, toColumn: string) => {
    if (toColumn === 'important_reading') { await moveImportant(item, true); return; }
    const status = EMAIL_STATUS_OF[toColumn];
    if (!status || (status === item.status && !item.important)) return;
    const prev = { status: item.status, important: item.important };
    setBusyEmail(item.bugReportId);
    setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId ? { ...it, status, important: false } : it)));
    try {
      if (item.important) await setInboxImportant(item.bugReportId, false);
      if (status !== item.status) await setInboxStatus(item.bugReportId, status);
      // A handled Important Reading item leaves the board (see emailColumnOf), so only nudge the rest.
      if (status === 'resolved' && !item.important) nudgeNotes(`m${item.bugReportId}`);
    } catch (e: any) {
      setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId ? { ...it, ...prev } : it)));
      toast.error(e?.message ?? 'Move failed');
    } finally {
      setBusyEmail(null);
    }
  }, [moveImportant, nudgeNotes]);

  const toggleRead = useCallback(async (item: InboxItem) => {
    const next = !item.hasRead;
    setBusyEmail(item.bugReportId);
    setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId
      ? { ...it, hasRead: next, readCount: Math.max(0, it.readCount + (next ? 1 : -1)) } : it)));
    try {
      await setInboxRead(item.bugReportId, next);
    } catch (e: any) {
      setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId
        ? { ...it, hasRead: !next, readCount: Math.max(0, it.readCount + (next ? -1 : 1)) } : it)));
      toast.error(e?.message ?? 'Failed to update read state');
    } finally {
      setBusyEmail(null);
    }
  }, []);

  const openReaders = useCallback(async (item: InboxItem) => {
    setReaders({ item, list: [], loading: true });
    try {
      const list = await fetchInboxReaders(item.bugReportId);
      setReaders({ item, list, loading: false });
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to load readers');
      setReaders(null);
    }
  }, []);

  const toggleNotes = useCallback((key: string) => {
    setNotesOpen((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
    setCompletedNudge((s) => { if (!s.has(key)) return s; const n = new Set(s); n.delete(key); return n; });
  }, []);
  const saveJobNotes = useCallback(async (job: DeskJob, notes: string) => {
    const saved = await saveDeskNotes('job', job.jobIdx, notes);
    patchJob(job.jobIdx, { notes: saved.notes, notesUpdatedBy: saved.notesUpdatedBy ?? '', notesUpdatedAt: saved.notesUpdatedAt ?? '' });
  }, [patchJob]);
  const saveEmailNotes = useCallback(async (item: InboxItem, notes: string) => {
    const saved = await saveDeskNotes('email', item.bugReportId, notes);
    setInbox((list) => list.map((it) => (it.bugReportId === item.bugReportId
      ? { ...it, notes: saved.notes, notesUpdatedBy: saved.notesUpdatedBy ?? '', notesUpdatedAt: saved.notesUpdatedAt ?? '' } : it)));
  }, []);

  if (!ready) return null;
  if (!canView) {
    return (
      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>{title}</div></div></div>
        <div className="bd"><div className="muted">{teamView ? 'Admin team access required.' : 'Admin leadership access required.'}</div></div>
      </section>
    );
  }

  // An Admin Team email card — used by every column an email can sit in. `c` is the
  // column's accent color. Read-tracking controls show only on Important cards; every
  // other email card moves via its Lane dropdown.
  const renderEmailCard = (item: InboxItem, c: string) => (
    <div
      key={`m${item.bugReportId}`}
      style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${c}`, borderRadius: 10, padding: '9px 11px', background: 'rgba(255,255,255,.03)', opacity: busyEmail === item.bugReportId ? 0.6 : 1 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Mail size={12} style={{ color: c, flex: '0 0 auto' }} />
        <span style={{ fontSize: 11, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.from}</span>
        {item.threadCount > 0 && (
          <span className="badge" title={`${item.threadCount} more message(s) in this thread`} style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, color: c, background: `color-mix(in srgb, ${c} 16%, transparent)` }}>+{item.threadCount}</span>
        )}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.subject}</div>
      {item.preview && (
        <div className="muted" style={{ fontSize: 10.5, marginTop: 3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.preview}</div>
      )}
      {fmtDue(item.receivedAt || '') && (
        <div className="muted" style={{ fontSize: 9.5, marginTop: 3, whiteSpace: 'nowrap' }}>received {fmtDue(item.receivedAt || '')}</div>
      )}
      {item.attachments.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <strong style={{ fontSize: 10.5 }}>Attachments</strong>
          <AttachmentChips bugReportId={item.bugReportId} attachments={item.attachments} />
        </div>
      )}

      {/* Read acknowledgment — Important Reading only. Every admin can check it off;
          only leadership sees who has. */}
      {item.important && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
          <label
            title={item.hasRead ? 'You acknowledged reading this — uncheck to undo' : 'Check to acknowledge you have read this'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 10.5, fontWeight: 700, color: item.hasRead ? '#57d38c' : 'inherit' }}
          >
            <input
              type="checkbox" checked={item.hasRead} disabled={busyEmail === item.bugReportId}
              onChange={() => toggleRead(item)}
              style={{ margin: 0, accentColor: '#57d38c', cursor: 'pointer' }}
            />
            I've read this
          </label>
          {canManageImportant && (
            <button
              type="button" onClick={() => openReaders(item)} title="See who has acknowledged reading this"
              style={{ all: 'unset', cursor: 'pointer', marginLeft: 'auto', fontSize: 10, color: 'var(--muted)' }}
            >
              {item.readCount} read ▸
            </button>
          )}
        </div>
      )}

      {/* Assigned To */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7 }}>
        <User size={12} style={{ color: 'var(--muted)', flex: '0 0 auto' }} />
        <select
          value={item.assignedTo ?? ''}
          disabled={busyEmail === item.bugReportId || (item.important && !canManageImportant)}
          onChange={(e) => assignEmail(item, e.target.value)}
          style={{ ...selectStyle, flex: 1, fontSize: 11, padding: '3px 5px' }}
          title="Assign this email to an admin"
        >
          <option value="" style={optionStyle}>— Unassigned —</option>
          {inboxPeople.map((p) => <option key={p.email} value={p.email} style={optionStyle}>{p.name}</option>)}
        </select>
      </div>

      {/* Lane — Important Reading cards are only movable by leadership (leadership view) */}
      {(!item.important || canManageImportant) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
          <span className="muted" style={{ fontSize: 10 }}>Lane</span>
          <select
            value={emailColumnOf(item) ?? ''}
            disabled={busyEmail === item.bugReportId}
            onChange={(e) => moveEmail(item, e.target.value)}
            style={{ ...selectStyle, marginLeft: 'auto', fontSize: 11, padding: '2px 4px' }}
            title="Move to another lane"
          >
            {EMAIL_LANES.filter((l) => l.key !== 'important_reading' || canManageImportant).map((l) => <option key={l.key} value={l.key} style={optionStyle}>{l.label}</option>)}
          </select>
        </div>
      )}

      {/* Notes */}
      <DeskNotes
        notes={item.notes} updatedBy={item.notesUpdatedBy} updatedAt={item.notesUpdatedAt} nameOf={inboxNameOf}
        open={notesOpen.has(`m${item.bugReportId}`)} onToggle={() => toggleNotes(`m${item.bugReportId}`)}
        hint={completedNudge.has(`m${item.bugReportId}`) ? 'Marked completed — add a note?' : undefined}
        onSave={(n) => saveEmailNotes(item, n)}
      />

      {/* actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setThreadView(item)}
          title={item.threadCount > 0
            ? `Open the email thread — ${item.threadCount} ${item.threadCount === 1 ? 'reply' : 'replies'} folded into this item`
            : 'Read the full saved email'}
          style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: 'var(--accent)' }}
        >
          <MoreHorizontal size={13} /> {item.threadCount > 0 ? `Thread · ${item.threadCount}` : 'Open email'}
        </button>
        <button
          type="button"
          onClick={() => openReply(item)}
          title="Email the original sender"
          style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: 'var(--accent)' }}
        >
          <Reply size={12} /> Reply
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LayoutGrid size={20} style={{ color: 'var(--accent)' }} />
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>{title}</div>
          </div>
          <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
            Admin-team jobs by workflow lane. Set <strong>Assigned To</strong> for who's working it; move a card to change its lane.
          </div>
        </div>
        <button className="btn" onClick={load} disabled={loading} title="Reload from Practice Engine">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '3px solid var(--bad)', fontSize: 13 }}>{error}</div>
      )}

      {/* controls */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client / job…"
            style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
        </div>
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Office
          <select value={officeFilter} onChange={(e) => setOfficeFilter(e.target.value)} style={selectStyle}>
            <option value="all" style={optionStyle}>All</option>
            {officeOptions.map((c) => <option key={c} value={c} style={optionStyle}>{officeName.get(c)}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Department
          <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} style={selectStyle}>
            <option value="all" style={optionStyle}>All</option>
            {deptOptions.map((d) => <option key={d} value={d} style={optionStyle}>{d}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Assigned to
          <select value={personFilter} onChange={(e) => setPersonFilter(e.target.value)} style={selectStyle}>
            <option value="all" style={optionStyle}>Anyone</option>
            {assignees.filter((a) => a.staffIndex !== NO_STAFF).map((a) => (
              <option key={a.staffIndex} value={String(a.staffIndex)} style={optionStyle}>{a.name}</option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          PE status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
            <option value="all" style={optionStyle}>All</option>
            {statusOptions.map((s) => <option key={s} value={s} style={optionStyle}>{s}</option>)}
          </select>
        </label>
        <button
          type="button" role="switch" aria-checked={showArchived} onClick={() => setShowArchived((v) => !v)}
          title={`Completed jobs auto-archive after ${ARCHIVE_DAYS} days. Searching or filtering still surfaces them.`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--muted)', fontSize: 12 }}
        >
          <span style={{ position: 'relative', width: 34, height: 18, borderRadius: 999, background: showArchived ? 'var(--accent)' : 'var(--border)', transition: 'background .15s', flexShrink: 0 }}>
            <span style={{ position: 'absolute', top: 2, left: showArchived ? 18 : 2, width: 14, height: 14, borderRadius: 999, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.3)', transition: 'left .15s' }} />
          </span>
          Show archived
        </button>
        <span className="muted" style={{ fontSize: 12 }}>{shownJobs} jobs</span>
      </div>

      {/* lanes */}
      {loading ? (
        <div className="card"><div className="bd"><div className="muted">Loading board…</div></div></div>
      ) : (
        <div style={{ overflowX: 'auto', paddingBottom: 6 }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(200px, 1fr))`, gap: 16, alignItems: 'start' }}>
          {columns.map((lane) => {
            const isInbox = lane.key === 'inbox_triage';
            const isImportant = lane.key === 'important_reading';
            const emailItems = emailsByColumn.get(lane.key) ?? [];
            const jobs = isInbox || isImportant ? [] : (jobsByBucket.get(lane.key) ?? []);
            const count = emailItems.length + jobs.length;
            const c = lane.color;
            return (
              <div key={lane.key} style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 2px 8px', borderBottom: `2px solid ${c}` }}>
                  <span style={{ width: 9, height: 9, borderRadius: 999, background: c, flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{lane.label}</span>
                  <span className="badge" style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: c, background: `color-mix(in srgb, ${c} 16%, transparent)` }}>{count}</span>
                </div>

                {isInbox && inboxError && (
                  <div style={{ fontSize: 11, padding: '8px 10px', borderRadius: 10, borderLeft: '3px solid var(--bad)', background: 'rgba(239,68,68,.08)' }}>
                    Couldn't load the Admin Team inbox: {inboxError}
                  </div>
                )}
                {count === 0 ? (
                  <div className="muted" style={{ fontSize: 12, padding: '20px 4px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 10 }}>{isImportant ? 'Nothing flagged' : isInbox ? 'No mail' : 'None'}</div>
                ) : (
                  <>
                  {emailItems.map((item) => renderEmailCard(item, c))}
                  {jobs.map((job) => (
                    <div
                      key={job.jobIdx}
                      style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${officeColor(job.officeCode)}`, borderRadius: 10, padding: '9px 11px', background: 'rgba(255,255,255,.03)', opacity: busyJob === job.jobIdx ? 0.6 : 1 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {job.clientName || job.clientCode}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          {job.sowUnsigned && (
                            <span title="On the “Holding to E-File — Missing Signed MSEL or SOW” hold"
                              style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 5, whiteSpace: 'nowrap', background: 'rgba(220,38,38,.18)', color: '#dc2626' }}>
                              SOW ✗
                            </span>
                          )}
                          {job.isPaper && (
                            <span title="A jurisdiction on this return is paper-filed"
                              style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 5, whiteSpace: 'nowrap', background: 'rgba(239,68,68,.18)', color: '#ef4444' }}>
                              PAPER-FILED
                            </span>
                          )}
                          <span title={job.deliveryMode ? `Delivery process (PE Assembly Instructions): ${job.assemblyMethod}` : 'No Assembly Instructions set in PE'}
                            style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 5, whiteSpace: 'nowrap', background: `color-mix(in srgb, ${deliveryColor(job.deliveryMode)} 18%, transparent)`, color: deliveryColor(job.deliveryMode) }}>
                            {job.deliveryMode === 'electronic' ? 'ELECTRONIC' : job.deliveryMode === 'paper' ? 'PAPER' : 'DELIVERY ?'}
                          </span>
                          <span title={`Department: ${job.dept || 'n/a'}`}
                            style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 5, whiteSpace: 'nowrap', background: `${deptColor(job.dept)}22`, color: deptColor(job.dept) }}>
                            {job.dept || '—'}
                          </span>
                        </div>
                      </div>
                      <div className="muted" style={{ fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.jobName}</div>
                      <div className="muted" style={{ fontSize: 10, marginTop: 3, whiteSpace: 'nowrap' }}>{job.office || job.officeCode}{job.jobClass ? ` · ${job.jobClass}` : ''}</div>
                      {job.workStatusDesc && (
                        <div style={{ marginTop: 5 }}>
                          <span title="Practice Engine work status"
                            style={{ display: 'inline-block', maxWidth: '100%', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom', background: `color-mix(in srgb, ${peStageColor(job.peStage)} 20%, transparent)`, color: peStageColor(job.peStage) }}>
                            {job.workStatusDesc}
                          </span>
                        </div>
                      )}
                      {/* Assembly / Shipping step status (derived from the PE work status) + method */}
                      <div style={{ marginTop: 5, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 8, rowGap: 2, fontSize: 10 }}>
                        {([
                          ['Assembly', job.assemblyStatus, job.deliveryMode === 'electronic' ? 'Electronic' : job.deliveryMode === 'paper' ? 'Paper' : ''],
                          ['Shipping', job.shippingStatus, job.shippingMethod],
                        ] as const).map(([label, status, method]) => (
                          <React.Fragment key={label}>
                            <span className="muted">{label}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              title={`${label}: ${STEP_LABEL[status]}${method ? ` — ${method}` : ''} (from PE work status)`}>
                              <span style={{ width: 7, height: 7, borderRadius: 999, background: stepColor(status), flexShrink: 0 }} />
                              <span style={{ fontWeight: 600, color: stepColor(status) }}>{STEP_LABEL[status]}</span>
                              {method && <span className="muted">· {method}</span>}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                      {fmtDue(job.currentDueDate) && (
                        <div className="muted" style={{ fontSize: 10, marginTop: 3, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          <Calendar size={10} /> Due {fmtDue(job.currentDueDate)}
                        </div>
                      )}

                      {/* Assigned To */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7 }}>
                        <User size={12} style={{ color: 'var(--muted)', flex: '0 0 auto' }} />
                        <select
                          value={job.staffIndex}
                          disabled={busyJob === job.jobIdx}
                          onChange={(e) => assign(job, Number(e.target.value))}
                          style={{ ...selectStyle, flex: 1, fontSize: 11, padding: '3px 5px' }}
                          title="Assigned to — who's working this job"
                        >
                          {assignees.map((a) => <option key={a.staffIndex} value={a.staffIndex} style={optionStyle}>{a.name}</option>)}
                        </select>
                      </div>
                      {fmtDue(job.assignedAt) && (
                        <div className="muted" style={{ fontSize: 9.5, marginTop: 2, marginLeft: 18 }} title={job.assignedBy || undefined}>
                          assigned {fmtDue(job.assignedAt)}{job.assignedBy ? ` · by ${actorLabel(job.assignedBy)}` : ''}
                        </div>
                      )}

                      {/* Lane — hidden for Admin Pool cards; assignment decides the lane there */}
                      {job.staffIndex === poolIndex ? (
                        <div className="muted" style={{ fontSize: 10, marginTop: 5, fontStyle: 'italic' }}>
                          In the Admin Pool — assign to a person to move it
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                            <span className="muted" style={{ fontSize: 10 }}>Lane</span>
                            <select
                              value={job.lane}
                              disabled={busyJob === job.jobIdx}
                              onChange={(e) => moveLane(job, e.target.value as Lane)}
                              style={{ ...selectStyle, marginLeft: 'auto', fontSize: 11, padding: '2px 4px' }}
                              title="Move to another lane"
                            >
                              {JOB_LANES.filter((l) => !teamView || l.key === 'completed').map((l) => <option key={l.key} value={l.key} style={optionStyle}>{l.label}</option>)}
                            </select>
                          </div>
                          {job.laneUpdatedBy && (
                            <div className="muted" style={{ fontSize: 9.5, marginTop: 2 }} title={job.laneUpdatedBy}>
                              moved by {actorLabel(job.laneUpdatedBy)}{fmtDue(job.laneUpdatedAt) ? ` · ${fmtDue(job.laneUpdatedAt)}` : ''}
                            </div>
                          )}
                        </>
                      )}

                      {/* Notes */}
                      <DeskNotes
                        notes={job.notes} updatedBy={job.notesUpdatedBy} updatedAt={job.notesUpdatedAt} nameOf={actorLabel}
                        open={notesOpen.has(`j${job.jobIdx}`)} onToggle={() => toggleNotes(`j${job.jobIdx}`)}
                        hint={completedNudge.has(`j${job.jobIdx}`) ? 'Marked completed — add a note?' : undefined}
                        onSave={(n) => saveJobNotes(job, n)}
                      />

                      {/* Open this job in Practice Engine */}
                      <a
                        href={`${peBase}/PE/Job/Job/${job.jobIdx}?panel=0`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open this job in Practice Engine"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 10, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}
                      >
                        <ExternalLink size={11} /> Open in PE
                      </a>
                    </div>
                  ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
        </div>
      )}

      {/* Email thread viewer (saved email + rolled-up replies + attachments) */}
      {threadView && <InboxThreadViewer item={threadView} nameOf={inboxNameOf} onClose={() => setThreadView(null)} />}

      {/* Reply-to-sender composer (Inbox Triage) */}
      {compose && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget && !sending) setCompose(null); }}
          style={{ position: 'fixed', inset: 0, background: 'var(--dm-scrim)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(3px)' }}
        >
          <div style={{ background: 'var(--dm-surface)', border: '1px solid var(--border)', borderRadius: 12, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--dm-shadow)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Reply to sender</div>
              <button className="btn" style={{ padding: '4px 8px' }} disabled={sending} onClick={() => setCompose(null)} aria-label="Close"><X size={14} /></button>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ fontSize: 11, color: 'var(--muted)' }}>To
                <input value={compose.to} placeholder="One or more addresses, separated by commas" onChange={(e) => setCompose({ ...compose, to: e.target.value })}
                  style={{ width: '100%', marginTop: 4, padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 11, color: 'var(--muted)' }}>Cc
                <input value={compose.cc} placeholder="Add people — separate addresses with commas"
                  onChange={(e) => setCompose({ ...compose, cc: e.target.value })}
                  style={{ width: '100%', marginTop: 4, padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
              </label>
              {inboxPeople.length > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    const addr = e.target.value;
                    if (!addr) return;
                    const have = `${compose.to},${compose.cc}`.toLowerCase().split(/[;,\s]+/);
                    if (have.includes(addr.toLowerCase())) return;
                    setCompose({ ...compose, cc: compose.cc.trim() ? `${compose.cc.trim().replace(/[;,]+$/, '')}, ${addr}` : addr });
                  }}
                  style={{ ...selectStyle, fontSize: 11, padding: '4px 6px', alignSelf: 'flex-start' }}
                  title="Add a teammate to Cc"
                >
                  <option value="" style={optionStyle}>+ Add a teammate to Cc…</option>
                  {inboxPeople.map((p) => <option key={p.email} value={p.email} style={optionStyle}>{p.name}</option>)}
                </select>
              )}
              <label style={{ fontSize: 11, color: 'var(--muted)' }}>Subject
                <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
                  style={{ width: '100%', marginTop: 4, padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 11, color: 'var(--muted)' }}>Message
                <textarea value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} rows={8}
                  style={{ width: '100%', marginTop: 4, padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
              </label>
              {quoteOriginal({ from: compose.item.from, when: compose.item.receivedAt, subject: compose.item.subject, body: compose.item.preview }) && (
                <div className="muted" style={{ fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto', padding: '6px 9px', borderRadius: 6, border: '1px dashed var(--border)' }}>
                  <div style={{ fontSize: 10, marginBottom: 4 }}>The original message will be included below your reply:</div>
                  {quoteOriginal({ from: compose.item.from, when: compose.item.receivedAt, subject: compose.item.subject, body: compose.item.preview })}
                </div>
              )}
              <div className="muted" style={{ fontSize: 10 }}>Sent as you{email ? ` (${email})` : ''} — replies come back to you.</div>
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" disabled={sending} onClick={() => setCompose(null)}>Cancel</button>
              <button className="btn primary" disabled={sending} onClick={sendReply}>{sending ? 'Sending…' : 'Send'}</button>
            </div>
          </div>
        </div>
      )}

      {/* "Who's read it" viewer (Important Reading) */}
      {readers && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setReaders(null); }}
          style={{ position: 'fixed', inset: 0, background: 'var(--dm-scrim)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(3px)' }}
        >
          <div style={{ background: 'var(--dm-surface)', border: '1px solid var(--border)', borderRadius: 12, width: 420, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--dm-shadow)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Eye size={15} /> Read receipts
                {!readers.loading && (
                  <span className="badge" style={{ fontSize: 10, fontWeight: 700, color: '#57d38c', background: 'color-mix(in srgb, #57d38c 16%, transparent)' }}>
                    {readRoster.filter((r) => r.read).length} of {readRoster.length} read
                  </span>
                )}
              </div>
              <button className="btn" style={{ padding: '4px 8px' }} onClick={() => setReaders(null)} aria-label="Close"><X size={14} /></button>
            </div>
            <div style={{ padding: 16 }}>
              <div className="muted" style={{ fontSize: 11, marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{readers.item.subject}</div>
              {readers.loading ? (
                <div className="muted" style={{ fontSize: 12 }}>Loading…</div>
              ) : readRoster.length === 0 ? (
                <div className="muted" style={{ fontSize: 12 }}>No admin roster to show.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {readRoster.map((r) => (
                    <div key={r.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12.5 }}>
                      <span title={r.email} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                      {r.read ? (
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#57d38c', flexShrink: 0 }}>
                          ✓ Read{fmtDue(r.readAt || '') ? ` · ${fmtDue(r.readAt || '')}` : ''}
                        </span>
                      ) : (
                        <span className="muted" style={{ fontSize: 10.5, flexShrink: 0 }}>Not yet</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
